import React from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/Card'
import { PermissionGate } from '@/components/PermissionGate'
import { 
  FileText, 
  Calendar, 
  ChefHat, 
  MapPin, 
  Users, 
  BarChart3,
  Plus,
  Eye,
  ArrowRight
} from 'lucide-react'

const quickAccessCards = [
  {
    title: 'Vistorias',
    description: 'Gerenciar vistorias do sistema',
    icon: FileText,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    permission: 'vistoria_view',
    links: [
      { label: 'Ver todas', path: '/vistorias', icon: Eye, permission: 'vistoria_view' },
      { label: 'Nova vistoria', path: '/vistorias/novo', icon: Plus, permission: 'vistoria_create' }
    ]
  },
  {
    title: 'Calendários',
    description: 'Gerenciar calendários acadêmicos',
    icon: Calendar,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    permission: 'calendario_view',
    links: [
      { label: 'Ver calendários', path: '/calendarios', icon: Eye, permission: 'calendario_view' },
      { label: 'Novo calendário', path: '/calendarios/novo', icon: Plus, permission: 'calendario_create' }
    ]
  },
  {
    title: 'Cardápios',
    description: 'Gerenciar cardápios do refeitório',
    icon: ChefHat,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    permission: 'cardapio_view',
    links: [
      { label: 'Ver cardápios', path: '/cardapios', icon: Eye, permission: 'cardapio_view' },
      { label: 'Novo cardápio', path: '/cardapios/novo', icon: Plus, permission: 'cardapio_create' }
    ]
  },
  {
    title: 'Geolocalização',
    description: 'Gerenciar localizações geográficas',
    icon: MapPin,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    permission: 'geolocation_view',
    links: [
      { label: 'Ver localizações', path: '/geolocations', icon: Eye, permission: 'geolocation_view' },
      { label: 'Nova localização', path: '/geolocation', icon: Plus, permission: 'geolocation_create' }
    ]
  },
  {
    title: 'Usuários',
    description: 'Gerenciar usuários do sistema',
    icon: Users,
    iconColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    permission: 'user_view',
    links: [
      { label: 'Ver usuários', path: '/usuarios', icon: Eye, permission: 'user_view' },
      { label: 'Novo usuário', path: '/usuarios/novo', icon: Plus, permission: 'user_create' }
    ]
  }
]

const statsCards = [
  { label: 'Total de Vistorias', value: '24', icon: FileText, color: 'purple' },
  { label: 'Calendários Ativos', value: '8', icon: Calendar, color: 'blue' },
  { label: 'Cardápios Cadastrados', value: '12', icon: ChefHat, color: 'green' },
  { label: 'Localizações', value: '156', icon: MapPin, color: 'red' }
]

export function Dashboard() {
  return (
    <div className="w-full">
      {/* Cabeçalho */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Visão geral do sistema de gestão</p>
      </div>

      {/* Seção de debug e informações do usuário removidas para produção */}

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((stat) => {
          const Icon = stat.icon
          return (
            <Card key={stat.label} className="p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg bg-${stat.color}-50 mr-4`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Acesso Rápido */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Acesso Rápido</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quickAccessCards.map((card) => {
            const Icon = card.icon
            return (
              <PermissionGate key={card.title} permission={card.permission}>
                <Card 
                  className={`p-6 hover:shadow-lg transition-shadow duration-200 ${card.bgColor} ${card.borderColor} border`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg bg-white shadow-sm`}>
                      <Icon className={`w-8 h-8 ${card.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {card.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {card.description}
                      </p>
                      
                      {/* Links de ação */}
                      <div className="space-y-2">
                        {card.links.map((link) => {
                          const LinkIcon = link.icon
                          return (
                            <PermissionGate key={link.path} permission={link.permission}>
                              <Link
                                to={link.path}
                                className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 group"
                              >
                                <LinkIcon className="w-4 h-4" />
                                <span>{link.label}</span>
                                <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </Link>
                            </PermissionGate>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </Card>
              </PermissionGate>
            )
          })}
        </div>
      </div>

      {/* Atividade Recente */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Atividade Recente</h2>
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Nova vistoria criada</p>
                <p className="text-xs text-gray-500">Há 2 horas</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Calendário acadêmico atualizado</p>
                <p className="text-xs text-gray-500">Há 4 horas</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <ChefHat className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Cardápio da semana publicado</p>
                <p className="text-xs text-gray-500">Ontem</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
